<template>
  <div>
    <NoAccessError v-if="presentLevelsStore.showAccessError" />
    <div v-else>
      <GbBreadcrumbs
        v-if="libraryFolder?.uuid"
        :items="breadcrumbItems"
        class="mt-6 -mb-4"
      />
      <PresentLevelsHeader
        title="Present Levels Statement"
      />
      
      <!-- Loading state that mimics the regular display -->
      <div v-if="!summary" class="flex flex-col lg:flex-row justify-center">
        <div class="relative">
          <PresentlevelsStudentHeader
            student-name="Loading..."
            grade-label="Loading..."
            :show-restart="false"
          />
          <SubjectSummaryLoading />
        </div>
        <div class="flex flex-col">
          <div class="w-plw-subcontent-panel ml-4 mb-2">
            <LibraryContentPanel
              content-uuid="loading"
              :content-type="CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY"
              :save-status="PL_SUMMARY_SAVING_STATES.NO_CHANGES"
              updated-at=""
            />
          </div>
        </div>
      </div>
      
      <!-- Regular content -->
      <div v-else class="flex flex-col">
        <!-- Show loading while waiting for libraryFolder -->
        <div v-if="!libraryFolder" class="flex flex-col lg:flex-row justify-center">
          <div class="relative">
            <PresentlevelsStudentHeader
              student-name="Loading..."
              grade-label="Loading..."
              :show-restart="false"
            />
            <SubjectSummaryLoading />
          </div>
          <div class="flex flex-col">
            <div class="w-plw-subcontent-panel ml-4 mb-2">
              <LibraryContentPanel
                content-uuid="loading"
                :content-type="CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY"
                :save-status="PL_SUMMARY_SAVING_STATES.NO_CHANGES"
                updated-at=""
              />
            </div>
          </div>
        </div>
        <router-view v-else :summary="summary" />
        <div class="self-end w-2/5 mt-4 mr-60">
          <AIDisclaimer />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AIDisclaimer from '@/components/AIDisclaimer.vue'
import { computed, ComputedRef, onMounted, watch } from 'vue'
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store.ts'
import PresentLevelsHeader from '@/presentlevels/PresentLevelsHeader.vue'
import GbBreadcrumbs from '@/components/GbBreadcrumbs.vue'
import { useRoute } from 'vue-router'
import type { SubjectSummary } from '@/presentlevels/presentlevels_types'
import { useLibraryStore } from '@/library/library.store.ts'
import { BreadcrumbItem } from '@/components/GbBreadcrumbs.vue'
import NoAccessError from '@/components/NoAccessError.vue'
import SubjectSummaryLoading from '@/presentlevels/SubjectSummaryLoading.vue'
import PresentlevelsStudentHeader from '@/presentlevels/PresentlevelsStudentHeader.vue'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants'

const presentLevelsStore = usePresentLevelsStore()
const libraryStore = useLibraryStore()
const route = useRoute()

const summaryUUID = computed(() => route.params.summary_uuid as string)
const summary: ComputedRef<SubjectSummary | undefined> = computed(() => {
  return presentLevelsStore.subjectSummaries.find((s: SubjectSummary) => s.uuid === summaryUUID.value)
})
const libraryFolder = computed(() => {
  if (!summaryUUID.value) {
    return undefined
  }
  return libraryStore.getFolderByContentUuid(summaryUUID.value)
})

watch(() => libraryFolder.value?.uuid, () => {
  if (libraryFolder.value?.uuid) {
    libraryStore.setCurrentFolderByUuid(libraryFolder.value.uuid)
  }
})

const fetchSummaryData = async () => {
  if (summaryUUID.value) {
    await presentLevelsStore.fetchSummaryByUUID(summaryUUID.value)
    await libraryStore.fetchFoldersForPLSubjectSummaries()
  }
}

onMounted(fetchSummaryData)

watch(summaryUUID, fetchSummaryData)


const breadcrumbItems: ComputedRef<BreadcrumbItem[]> = computed(() => {
  const items: BreadcrumbItem[] = [
    {
      label: 'Library: All Folders',
      to: { name: 'Library' }
    },
  ]
  if (libraryFolder.value) {
    items.push({
      label: libraryFolder.value?.name || 'Folder',
      icon: 'folder',
      to: {
        name: 'LibraryFolder',
        params: { uuid: libraryFolder.value.uuid }
      }
    })
  }
  if (summary.value) {
    items.push({
      label: `Present Levels: ${summary.value.subject_label}`,
      to: undefined
    })
  }
  return items
})
</script>
